/* import __COLOCATED_TEMPLATE__ from './select-email-origins.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { type PulseAccordion } from 'glint/pulse';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isPresent, isEmpty } from '@ember/utils';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import { post } from 'embercom/lib/ajax';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';

interface Args {
  accordion: PulseAccordion;
  onContinue: () => void;
}

export default class StandaloneSalesforceSelectEmailOrigins extends Component<Args> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked newlyAddedCaseOrigins: Array<string> = [];
  @tracked newlyRemovedCaseOrigins: Array<string> = [];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.maybeAutoSelectEmailOrigin();
  }

  async maybeAutoSelectEmailOrigin() {
    if (this.appService.app.hasOptionalConfigureEmails) {
      return;
    }

    await Promise.all([
      taskFor(this.finStandaloneService.salesforceDataLoader.loadSalesforceSetupData).last!,
      taskFor(this.finStandaloneService.salesforceDataLoader.loadSalesforceConfig).last!,
    ]);

    let emailOrigin = this.allCaseOrigins.find((origin) => {
      return origin.label.toLowerCase() === 'email';
    });

    if (emailOrigin && isEmpty(this.persistedSelection) && isEmpty(this.newlyAddedCaseOrigins)) {
      this.newlyAddedCaseOrigins = [emailOrigin.value];
    }
  }

  get app() {
    return this.appService.app;
  }

  get salesforceConfig() {
    return this.finStandaloneService.salesforceConfig;
  }

  get salesforceSetupData() {
    return this.finStandaloneService.salesforceSetupData;
  }

  get isLoading() {
    return (
      this.finStandaloneService.salesforceDataLoader.isLoadingSalesforceConfig ||
      this.finStandaloneService.salesforceDataLoader.isLoadingSalesforceSetupData
    );
  }

  get isDisabled() {
    return (
      this.salesforceConfig?.isPendingSetup ||
      !this.salesforceSetupData?.hasPermissionSetAssignedToInstaller
    );
  }

  get isComplete() {
    return this.salesforceConfig?.isConnected && isPresent(this.persistedSelection);
  }

  get persistedSelection() {
    return this.finStandaloneService.selectedSalesforceCaseOrigins;
  }

  get allCaseOrigins() {
    return this.salesforceSetupData.caseOrigins.map((origin) => {
      if (typeof origin === 'string') {
        // temporary logic to handle an array of strings response from the backend
        return { value: origin, label: origin };
      } else {
        return origin;
      }
    });
  }

  get enabledCaseOrigins() {
    return [...this.persistedSelection, ...this.newlyAddedCaseOrigins]
      .uniq()
      .filter((origin) => !this.newlyRemovedCaseOrigins.includes(origin));
  }

  get hasUpdatedOrigins() {
    return isPresent(this.newlyAddedCaseOrigins) || isPresent(this.newlyRemovedCaseOrigins);
  }

  @action
  continue() {
    if (this.args.onContinue) {
      this.args.onContinue();
    }
  }

  @action openSectionChanged() {
    this.newlyAddedCaseOrigins = [];
    this.newlyRemovedCaseOrigins = [];
  }

  @action toggleCaseOrigin(origin: string) {
    let isCurrentlyEnabled = this.persistedSelection.includes(origin);

    if (isCurrentlyEnabled) {
      let isLocallyDisabled = this.newlyRemovedCaseOrigins.some((o) => o === origin);

      if (isLocallyDisabled) {
        this.newlyRemovedCaseOrigins = this.newlyRemovedCaseOrigins.filter((o) => o !== origin);
      } else {
        this.newlyRemovedCaseOrigins.pushObject(origin);
        this.newlyAddedCaseOrigins.removeObject(origin);
      }
    } else {
      let isLocallyEnabled = this.newlyAddedCaseOrigins.some((o) => o === origin);

      if (isLocallyEnabled) {
        this.newlyAddedCaseOrigins = this.newlyAddedCaseOrigins.filter((o) => o !== origin);
      } else {
        this.newlyAddedCaseOrigins.pushObject(origin);
        this.newlyRemovedCaseOrigins.removeObject(origin);
      }
    }
  }

  async addSalesforceCaseOrigin(origin: string) {
    try {
      await post(
        `/ember/standalone/salesforce_configurations/${this.app.id}/create_salesforce_case_origin`,
        {
          app_id: this.app.id,
          origin,
        },
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.notifications.fin-service-errors.case-origin-add-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
    }
  }

  async removeSalesforceCaseOrigin(origin: string) {
    try {
      await post(
        `/ember/standalone/salesforce_configurations/${this.app.id}/delete_salesforce_case_origin`,
        {
          app_id: this.app.id,
          origin,
        },
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t(
            'standalone.notifications.fin-service-errors.case-origin-remove-error',
          ),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
    }
  }

  @action async continueAndMaybeSave() {
    try {
      if (this.hasUpdatedOrigins) {
        this.newlyAddedCaseOrigins.forEach(async (origin) => {
          await this.addSalesforceCaseOrigin(origin);
        });

        this.newlyRemovedCaseOrigins.forEach(async (origin) => {
          await this.removeSalesforceCaseOrigin(origin);
        });

        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'standalone.deploy.salesforce.cases.configure-emails.select-email-origins-section.success-notification',
          ),
        );

        this.finStandaloneService.selectedSalesforceCaseOrigins = this.enabledCaseOrigins;
      }

      this.newlyAddedCaseOrigins = [];
      this.newlyRemovedCaseOrigins = [];

      this.continue();
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t(
          'standalone.deploy.salesforce.cases.configure-emails.select-email-origins-section.error-notification',
        ),
      });
    }
  }

  get saveDisabled() {
    return (
      this.salesforceConfig?.isPendingSetup ||
      (this.newlyAddedCaseOrigins.length === 0 && this.newlyRemovedCaseOrigins.length === 0)
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::ConfigureEmails::SelectEmailOrigins': typeof StandaloneSalesforceSelectEmailOrigins;
    'standalone/deploy/salesforce/cases/configure-emails/select-email-origins': typeof StandaloneSalesforceSelectEmailOrigins;
  }
}
